import {
    ChatMessage,
    SendFileMessage, SendMediumMessage,
    SendMessage,
    SendTextMessage, SendVoiceMessage
} from "@web-libs/features/chat/domain/types/chat-message.types";
import {v4 as uuidv4} from "uuid";
import {ChatRepository} from "@web-libs/features/chat/domain/chat.repository";
import {createGeneralOptimisticMessage} from "@web-libs/features/chat/application/messenger/create-optimistic-message";
import {buildErrorHandler} from "@web-libs/features/chat/application/messenger/sending-error-handler";
import {Chat} from "@web-libs/features/chat/domain/types/chat.type";
import {Dispatch, SetStateAction} from "react";
import {setPendingTimeout} from "@web-libs/features/chat/application/messenger/pending-timeout";
import {useMessageResending} from "@web-libs/features/chat/application/messenger/use-message-resending";
import {Chatroom} from "@web-libs/features/chat/domain/types/chatroom.type";
import {useOwnUser} from "@web-libs/features/users/application/use-own-user.concern";

type FinishOptimisticMessage = (message: ChatMessage) => ChatMessage;
type SendMessageFunction<Payload extends SendMessage> = (chatroomId: string, payload: Payload) => Promise<void>;
export type UpsertChatMessage = (chatroomId: string, message: ChatMessage) => void;
export type SetChatFunction = Dispatch<SetStateAction<Chat>>;
export const useChatMessenger = (setChatFunction: SetChatFunction, upsertChatMessage: UpsertChatMessage, chatRepository: ChatRepository, chatrooms: Chatroom[]) => {

    const _withSendingErrorHandler = buildErrorHandler(upsertChatMessage);
    const ownUserConcern = useOwnUser()

    const _sendMessage = async <Payload extends SendMessage>(
        chatroomId: string,
        finishOptimisticMessage: FinishOptimisticMessage,
        sendMessage: Payload,
        sendMessageFunction: SendMessageFunction<Payload>
    ) => {
        const internalReferenceId = uuidv4();
        const ownUserId = await ownUserConcern.getId();
        const userName = await ownUserConcern.getName();
        const optimisticMessage = createGeneralOptimisticMessage(sendMessage, internalReferenceId,ownUserId, userName ?? "Du")
        const completeOptimisticMessage = finishOptimisticMessage(optimisticMessage);
        upsertChatMessage(chatroomId, completeOptimisticMessage);
        const sendMessageWithInternalReferenceId = {
            ...sendMessage,
            internalReferenceId: internalReferenceId
        }
        await _withSendingErrorHandler(
            () => sendMessageFunction(chatroomId, sendMessageWithInternalReferenceId),
            completeOptimisticMessage,
            chatroomId
        );
        setPendingTimeout(setChatFunction, chatroomId, internalReferenceId)
    }

    const sendTextMessage = async (chatroomId: string, sendMessage: SendTextMessage) => {
        const finishOptimisticMessage: FinishOptimisticMessage = (message) => ({
            ...message,
            text: sendMessage.text
        })
        await _sendMessage<SendTextMessage>(chatroomId, finishOptimisticMessage, sendMessage, chatRepository.sendMessage);
    }

    const sendFileMessage = async (chatroomId: string, sendMessage: SendFileMessage) => {
        const finishOptimisticMessage: FinishOptimisticMessage = (message) => ({
            ...message,
            fileBackupForResend: sendMessage.file,
            contentUrl: URL.createObjectURL(sendMessage.file),
        })
        await _sendMessage<SendFileMessage>(chatroomId, finishOptimisticMessage, sendMessage, chatRepository.sendFile);
    }

    const sendMediaMessage = async (chatroomId: string, sendMessage: SendMediumMessage) => {
        const finishOptimisticMessage: FinishOptimisticMessage = (message) => ({
            ...message,
            contentUrl: URL.createObjectURL(sendMessage.medium),
            fileBackupForResend: sendMessage.medium,
        })
        await _sendMessage<SendMediumMessage>(chatroomId, finishOptimisticMessage, sendMessage, chatRepository.sendMedium);
    }

    const sendVoiceMessage = async (chatroomId: string, sendMessage: SendVoiceMessage) => {
        const finishOptimisticMessage: FinishOptimisticMessage = (message) => ({
            ...message,
            contentUrl: URL.createObjectURL(sendMessage.blob),
            blobBackupForResend: sendMessage.blob,
            duration: sendMessage.duration
        })
        await _sendMessage<SendVoiceMessage>(chatroomId, finishOptimisticMessage, sendMessage, chatRepository.sendVoice);
    }

    const {resendMessage} = useMessageResending(setChatFunction, upsertChatMessage, chatRepository);

    return {
        sendTextMessage,
        sendFileMessage,
        sendMediaMessage,
        sendVoiceMessage,
        resendMessage
    }
}




